import { Configuration, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import { logger } from "../services/logger.service";

/**
 * MSAL Configuration for Azure AD B2C
 *
 * This configuration supports:
 * - Azure AD B2C multi-policy flows (signin, resetpassword)
 * - Token caching in localStorage/sessionStorage
 * - Comprehensive logging for debugging
 * - MSAL Browser v4 async initialization pattern
 */

export interface B2CConfig {
  instance: string;
  tenant: string;
  clientId: string;
  cacheLocation: "localStorage" | "sessionStorage";
  scopes: string[];
  policies: {
    signin: string;
    resetpassword: string;
    [key: string]: string;
  };
  redirectUri: string;
  postLogoutRedirectUri: string;
}

/**
 * Default scopes required for all authentication flows
 */
const defaultScopes = ["openid"];

/**
 * Creates MSAL configuration from B2C settings
 * @param config B2C configuration object
 * @returns MSAL Configuration object
 */
export function createMsalConfig(config: B2CConfig): Configuration {
  // Merge custom scopes with default scopes (deduplicated)
  const loginScopes = Array.from(new Set([...config.scopes, ...defaultScopes]));

  return {
    auth: {
      authority: config.instance,
      clientId: config.clientId,
      redirectUri: config.redirectUri,
      knownAuthorities: Object.values(config.policies).map((policy) => {
        return config.instance.replace(config.policies.signin, policy);
      }),
      navigateToLoginRequestUrl: false,
      postLogoutRedirectUri: config.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: config.cacheLocation,
      storeAuthStateInCookie: true, // Store auth state in cookie to prevent cache errors
      secureCookies: window.location.protocol === "https:",
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          // Never PII; and in a built bundle, whose console anyone can open, only MSAL's errors.
          if (containsPii || (!import.meta.env.DEV && level !== LogLevel.Error)) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
          }
        },
        logLevel: LogLevel.Warning,
      },
    },
  };
}

/**
 * Where MSAL keeps the sign-in. An empty or unknown value must not reach MSAL: it treats anything
 * it doesn't recognise as in-memory storage, so the sign-in is lost on every refresh and new tab
 * and each one goes back through the Microsoft login. The production build passed no value, which
 * is exactly that. localStorage is what DEV uses.
 */
export function resolveCacheLocation(value: string | undefined): "localStorage" | "sessionStorage" {
  return value === "sessionStorage" ? "sessionStorage" : "localStorage";
}

/**
 * Initialize B2C configuration from environment variables
 */
export function initializeB2CConfig(): B2CConfig {
  return {
    instance: import.meta.env.VITE_B2C_CONFIG_INSTANCE,
    tenant: import.meta.env.VITE_B2C_CONFIG_TENANT,
    clientId: import.meta.env.VITE_B2C_CONFIG_CLIENT_ID,
    cacheLocation: resolveCacheLocation(import.meta.env.VITE_B2C_CONFIG_CACHE_LOCATION),
    scopes: JSON.parse(import.meta.env.VITE_B2C_CONFIG_SCOPES || "[]"),
    policies: JSON.parse(import.meta.env.VITE_B2C_CONFIG_POLICIES || "{}"),
    redirectUri: window.location.origin + "/callback",
    postLogoutRedirectUri: window.location.href,
  };
}

/**
 * Create and initialize MSAL instance
 *
 * IMPORTANT: MSAL Browser v4 requires async initialization
 * This must be called before any authentication operations
 *
 * @returns Initialized PublicClientApplication instance
 */
export async function createMsalInstance(): Promise<PublicClientApplication> {
  const b2cConfig = initializeB2CConfig();

  const msalConfig = createMsalConfig(b2cConfig);

  const msalInstance = new PublicClientApplication(msalConfig);

  // MSAL v4 Breaking Change: initialize() must be called and awaited
  await msalInstance.initialize();

  // Proactively clear stale interaction state from a previous interrupted redirect.
  // If `interaction_in_progress` is in sessionStorage but the URL has no auth callback
  // (no code= or error= in hash/search), the state is stale and safe to remove.
  // This is the primary fix for the intermittent "Sign-in Requires a New Window" error
  // and for "Bad Request" errors when opening multiple email links in different tabs.
  const interactionKey = `msal.${b2cConfig.clientId}.interaction.status`;
  const hasStaleInteraction = sessionStorage.getItem(interactionKey) === "interaction_in_progress";
  const hasRedirectResponse =
    window.location.hash.includes("code=") ||
    window.location.hash.includes("error=") ||
    window.location.search.includes("code=");

  if (hasStaleInteraction && !hasRedirectResponse) {
    logger.warn("Clearing stale MSAL interaction state from previous session");
    sessionStorage.removeItem(interactionKey);
    // Also clear any orphaned MSAL request keys that may conflict with new auth flows
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith("msal.") && key.includes(".request.")) {
        sessionStorage.removeItem(key);
      }
    }
  }

  // Handle redirect promise to complete auth flow
  // This is CRITICAL to prevent no_token_request_cache_error
  try {
    await msalInstance.handleRedirectPromise();
  } catch (error) {
    logger.error("MSAL redirect handling failed", error instanceof Error ? error : undefined);
    // Clear all stale MSAL keys from sessionStorage to prevent stuck state on next attempt
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith("msal.")) {
        sessionStorage.removeItem(key);
      }
    }
  }

  return msalInstance;
}

/**
 * Get login scopes (including default scopes)
 */
export function getLoginScopes(b2cConfig: B2CConfig): string[] {
  return Array.from(new Set([...b2cConfig.scopes, ...defaultScopes]));
}

/**
 * Export singleton B2C config for use across the app
 */
export const b2cConfig = initializeB2CConfig();

/**
 * Export policies for B2C flows (signin, resetpassword)
 * Used by useB2CPolicies hook for policy-specific redirect handling
 */
export const policies = b2cConfig.policies;

/**
 * Export login scopes for use in authentication flows
 */
export const loginScopes = getLoginScopes(b2cConfig);

/**
 * Login request configuration for redirect flows
 * Used by ProtectedRoute and other authentication flows
 */
export const loginRequest = {
  scopes: loginScopes,
};
